import {
  UserPlus,
  Users,
  Zap,
  Eye,
  TrendingUp,
  Globe,
  Trophy,
  CheckCircle,
  CheckSquare,
  Rocket,
  Award,
  Flame,
  Lock,
} from 'lucide-react';
import type { BadgeDefinition, BadgeTier } from '../../types/gamification';
import { TIER_LABELS, TIER_COLORS } from '../../types/gamification';

// Icon mapping
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  UserPlus,
  Users,
  Magnet: Zap, // Using Zap as fallback for Magnet
  Zap,
  Eye,
  TrendingUp,
  Globe,
  Trophy,
  CheckCircle,
  CheckSquare,
  Rocket,
  Award,
  Flame,
};

interface BadgeCardProps {
  badge: BadgeDefinition;
  earned?: boolean;
  earnedAt?: string;
  current?: number;
  showProgress?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export function BadgeCard({
  badge,
  earned = false,
  earnedAt,
  current = 0,
  showProgress = false,
  size = 'md',
  onClick,
}: BadgeCardProps) {
  const IconComponent = ICONS[badge.icon] || Trophy;
  const tierColors = TIER_COLORS[badge.tier as BadgeTier];
  const progress = Math.min((current / badge.threshold) * 100, 100);

  const sizeClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
  };

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  return (
    <div
      className={`
        relative rounded-xl border ${sizeClasses[size]} transition-all
        ${earned
          ? `${tierColors.bg} ${tierColors.border} shadow-sm`
          : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 opacity-60'
        }
        ${onClick ? 'cursor-pointer hover:shadow-md' : ''}
      `}
      onClick={onClick}
    >
      {/* Badge Icon */}
      <div className="flex flex-col items-center text-center">
        <div
          className={`
            ${iconSizes[size]} mb-2 flex items-center justify-center
            ${earned ? tierColors.text : 'text-gray-400 dark:text-slate-500'}
          `}
        >
          {earned ? (
            <IconComponent className={iconSizes[size]} />
          ) : (
            <Lock className={iconSizes[size]} />
          )}
        </div>

        {/* Badge Name */}
        <h4
          className={`
            font-semibold ${textSizes[size]} leading-tight
            ${earned ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-slate-400'}
          `}
        >
          {badge.name}
        </h4>

        {/* Tier Label */}
        <span
          className={`
            text-[10px] uppercase tracking-wider mt-0.5
            ${earned ? tierColors.text : 'text-gray-400 dark:text-slate-500'}
          `}
        >
          {TIER_LABELS[badge.tier as BadgeTier]}
        </span>

        {/* Progress Bar (optional) */}
        {showProgress && !earned && (
          <div className="w-full mt-2">
            <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-1">
              {current} / {badge.threshold}
            </p>
          </div>
        )}

        {/* Earned Date */}
        {earned && earnedAt && size !== 'sm' && (
          <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
            {new Date(earnedAt).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>

      {/* Earned Checkmark */}
      {earned && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
          <CheckCircle className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
}
